/**
 * AddDeviceEnum Component
 * 
 * A comprehensive dialog component for adding new device types to the system.
 * Manages complex device data including:
 * - Basic info: name, type, subtitle, description
 * - Operating system
 * - Sensors configuration (name, type, unit, hardware)
 * - I/O configuration (name, type, hardware)
 * - Hardware components list
 * - Links (GitHub, website)
 * - Status flags (private, alpha, beta)
 * - Images: icon and image gallery (up to 5 images)
 * 
 * Features:
 * - Image upload with automatic compression/resizing
 * - Dynamic list management for sensors, I/O, and hardware
 * - Form validation
 * - Firebase Storage and Firestore integration
 * - Snackbar notifications for user feedback
 */
import React, { useState } from "react";

import { doc, collection, updateDoc, setDoc } from "firebase/firestore";
import { useDropzone } from "react-dropzone";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { Switch, Snackbar } from "@mui/material";
import Resizer from "react-image-file-resizer";

import {
  ref,
  getDownloadURL,
  deleteObject,
  uploadBytes,
} from "firebase/storage";

import { ImageLoader } from "../../../../components/ui/img/ImageLoader";
import { firebaseStorage } from "../../../../components/firebase/storage";
import { firebaseStore } from "../../../../components/firebase/firestore";
import { primaryColorTextField } from "../../../../components/theme/globalStyles";
import {
  deviceSensorEnums,
  deviceIoEnums,
} from "../../../../components/utils/enums";

import "./DeviceEnumsComp.css";

function AddDeviceEnum() {
  /**
   * Dropzone configuration for icon upload
   * Accepts: JPEG, PNG
   * Max files: 1
   */
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/png": [],
    },
    maxFiles: 1,
    onDrop: (files) => onIconDrop(files),
  });
  
  /**
   * Dropzone configuration for image gallery
   * Accepts: JPEG, PNG
   * Max files: 5 total
   */
  const {
    getRootProps: getRootPropsImageArray,
    getInputProps: getInputPropsImageArray,
  } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/png": [],
    },
    maxFiles: 5,
    onDrop: (files) => onImageDropArray(files),
  });
  
  // Dialog and UI state
  const [open, setOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Basic device information state
  const [deviceName, setDeviceName] = useState<string>("");
  const [deviceType, setDeviceType] = useState<string>("");
  const [deviceSubtitle, setDeviceSubtitle] = useState<string>("");
  const [deviceDescription, setDeviceDescription] = useState<string>("");
  const [deviceOS, setDeviceOS] = useState<string>("");
  
  // Image upload state
  const [iconPath, setIconPath] = useState<any>(null);
  const [imagePathArray, setImagePathArray] = useState<any[]>([]);
  
  // Sensors state
  // Each sensor has: name, type, unit, hardware
  const [sensorList, setSensorList] = useState<any[]>([]);
  const [sensorActualName, setSensorActualName] = useState<string>("");
  const [sensorActualType, setSensorActualType] = useState<string>("");
  const [sensorActualUnit, setSensorActualUnit] = useState<string>("");
  const [sensorActualHardware, setSensorActualHardware] = useState<string>("");
  
  // I/O state
  // Each I/O has: name, type, hardware
  const [ioList, setIoList] = useState<any[]>([]);
  const [ioActualName, setIoActualName] = useState<string>("");
  const [ioActualType, setIoActualType] = useState<string>("");
  const [ioActualHardware, setIoActualHardware] = useState<string>("");
  
  // Hardware components list
  const [hardwareList, setHardwareList] = useState<string[]>([]);
  const [hardwareActual, setHardwareActual] = useState<string>("");
  
  // External links
  const [githubLink, setGithubLink] = useState<string>("");
  const [webLink, setWebLink] = useState<string>("");
  
  // Status flags
  const [isPrivate, setIsPrivate] = useState<boolean>(false);
  const [isAlpha, setIsAlpha] = useState<boolean>(false);
  const [isBeta, setIsBeta] = useState<boolean>(false);

  /**
   * Close the add device dialog
   */
  const handleClose = () => {
    setOpen(false);
  };

  /**
   * Handle icon image drop from dropzone
   * @param files - Array of dropped files (max 1 for icon)
   */
  const onIconDrop = async (files: any) => {
    const file = files[0];
    setIconPath(file);
  };

  /**
   * Handle image gallery drop from dropzone
   * Validates max 5 images total
   * @param files - Array of dropped files
   */
  const onImageDropArray = async (files: any) => {
    if (files.length > 5) {
      return;
    }
    if (files.length + imagePathArray.length > 5) {
      return;
    }
    const tempArray = [...imagePathArray];
    files.forEach((file: any) => {
      tempArray.push(file);
    });
    setImagePathArray(tempArray);
  };

  /**
   * Remove an image from the image gallery array
   * @param index - Index of image to remove
   */
  const onDeleteFromImageArray = (index: number) => {
    const tempList = [...imagePathArray];
    tempList.splice(index, 1);
    setImagePathArray(tempList);
  };

  /**
   * Reset all form fields to their initial/default values
   * Called after successful submission or user cancellation
   */
  const onReset = () => {
    setDeviceName("");
    setDeviceType("");
    setDeviceSubtitle("");
    setDeviceDescription("");
    setGithubLink("");
    setWebLink("");
    setSensorList([]);
    setIoList([]);
    setHardwareList([]);
    setIsPrivate(false);
    setIsAlpha(false);
    setIsBeta(false);
    setIconPath(null);
    setImagePathArray([]);
    setHardwareActual("");
    setSensorActualName("");
    setSensorActualType("");
    setSensorActualUnit("");
    setIoActualName("");
    setIoActualType("");
    setSensorActualHardware("");
    setIoActualHardware("");
    setDeviceOS("");
  };

  /**
   * Resize image file using react-image-file-resizer
   * Compresses and resizes images before upload to reduce storage costs
   * 
   * @param file - Original image file
   * @param size - Target size (width and height in pixels)
   * @param type - Image format ('png' or 'jpeg')
   * @returns Promise resolving to resized blob
   */
  const resizeFile = (file: any, size: number, type: string) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        size,
        size,
        type,
        100,
        0,
        (uri) => {
          resolve(uri);
        },
        "blob"
      );
    });

  /**
   * Check if an image already exists in Firebase Storage
   * @param path - Storage path to check
   * @returns Promise<boolean> - true if exists, false otherwise
   */
  const checkIfLogoExists = async (path: string) => {
    try {
      const imageRef = ref(firebaseStorage, path);
      await getDownloadURL(imageRef);
      return true;
    } catch (err) {
      return false;
    }
  };

  /**
   * Upload an image to Firebase Storage with automatic resizing and compression
   * If file exists at path, it will be deleted first
   * 
   * @param file - Image file to upload
   * @param path - Firebase Storage path
   * @param size - Target size in pixels (square)
   * @param type - Image format ('png' or 'jpeg')
   * @returns Promise<string> - Download URL of uploaded image, or empty string on error
   */
  const uploadImage = async (
    file: any,
    path: string,
    size: number,
    type: string
  ) => {
    const storageRef = ref(firebaseStorage, path);
    try {
      const checkIfExists = await checkIfLogoExists(path);
      if (checkIfExists) {
        await deleteObject(storageRef);
      }
      const resizedFile: any = await resizeFile(file, size, type);
      await uploadBytes(storageRef, resizedFile);
      const downloadUrl = await getDownloadURL(storageRef);
      return downloadUrl;
    } catch (error) {
      console.log(error);
      return "";
    }
  };

  /**
   * Main function to upload device enum data to Firestore and Storage
   * 
   * Process:
   * 1. Creates Firestore document with device data using deviceType as ID
   * 2. Uploads icon image (if provided) to Storage
   * 3. Uploads image gallery (if provided) to Storage
   * 4. Updates Firestore document with image URLs
   * 5. Shows success notification
   * 
   * Images are automatically resized:
   * - Icon: 500x500px PNG
   * - Gallery images: 1000x1000px JPEG each
   */
  const uploadDeviceEnum = async () => {
    setDialogLoading(true);
    try {
      const deviceEnumTemp = {
        name: deviceName,
        type: deviceType,
        subtitle: deviceSubtitle,
        description: deviceDescription,
        sensors: sensorList,
        io: ioList,
        hardware: hardwareList,
        github: githubLink,
        web: webLink,
        private: isPrivate,
        alpha: isAlpha,
        beta: isBeta,
        os: deviceOS,
      };
      await setDoc(
        doc(collection(firebaseStore, "deviceEnums"), deviceType),
        deviceEnumTemp
      );
      let iconUrl = "";
      let imageUrlArray = [];
      if (iconPath) {
        const iconPathString = `cloud/deviceEnums/${deviceType}/${deviceType}-icon.png`;
        const uploadIcon = await uploadImage(
          iconPath,
          iconPathString,
          500,
          "png"
        );
        iconUrl = uploadIcon;
      }
      if (imagePathArray.length > 0) {
        for (let i = 0; i < imagePathArray.length; i++) {
          const imagePathString = `cloud/deviceEnums/${deviceType}/${deviceType}-${
            i + 1
          }.jpeg`;
          const uploadImageTemp = await uploadImage(
            imagePathArray[i],
            imagePathString,
            1000,
            "jpeg"
          );
          imageUrlArray.push(uploadImageTemp);
        }
      }
      await updateDoc(
        doc(collection(firebaseStore, "deviceEnums"), deviceType),
        {
          icon: iconUrl,
          images: imageUrlArray,
        }
      );

      setSnackbarOpen(true);
      setSnackbarMessage(`${deviceName} added to device enums`);
      setDialogLoading(false);
    } catch (error) {
      console.log(error);
      setDialogLoading(false);
    }
  };

  return (
    <div className="AddDeviceEnum">
      <IconButton sx={{ margin: 0 }} onClick={() => setOpen(true)}>
        <AddIcon style={{ color: "#44a99a" }} />
      </IconButton>
      <Dialog
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiDialog-paper": {
            width: "90vw",
            maxWidth: "1000px",
            display: "flex",
            alignItems: "center",
          },
        }}
      >
        <div className="AddPlantEnumDialog">
          <DialogTitle>Add device enum</DialogTitle>
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Device name"
              variant="outlined"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
            />
          </div>
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Type"
              variant="outlined"
              value={deviceType}
              onChange={(e) => setDeviceType(e.target.value)}
            />
          </div>
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Subtitle"
              variant="outlined"
              value={deviceSubtitle}
              onChange={(e) => setDeviceSubtitle(e.target.value)}
              multiline
              rows={2}
            />
          </div>
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Description"
              variant="outlined"
              value={deviceDescription}
              onChange={(e) => setDeviceDescription(e.target.value)}
              multiline
              rows={4}
            />
          </div>
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Github link"
              variant="outlined"
              value={githubLink}
              onChange={(e) => setGithubLink(e.target.value)}
            />
          </div>
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Web link"
              variant="outlined"
              value={webLink}
              onChange={(e) => setWebLink(e.target.value)}
            />
          </div>
          <div
            className="AddPlantEnumDialogInner"
            style={{ marginTop: "40px" }}
          >
            <p className="AddPlantEnumDialogSwitchText">Is private</p>
            <Switch
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#44a99a",
                },

                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#44a99a",
                },
              }}
            />
          </div>
          <div className="AddPlantEnumDialogInner">
            <p className="AddPlantEnumDialogSwitchText">Is alpha</p>
            <Switch
              checked={isAlpha}
              onChange={(e) => setIsAlpha(e.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#44a99a",
                },

                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#44a99a",
                },
              }}
            />
          </div>
          <div className="AddPlantEnumDialogInner">
            <p className="AddPlantEnumDialogSwitchText">Is beta</p>
            <Switch
              checked={isBeta}
              onChange={(e) => setIsBeta(e.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#44a99a",
                },

                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#44a99a",
                },
              }}
            />
          </div>
          <div
            className="AddPlantEnumDialogInner"
            style={{ marginTop: "40px" }}
          >
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="OS"
              variant="outlined"
              value={deviceOS}
              onChange={(e) => setDeviceOS(e.target.value)}
            />
          </div>
          <div className="AddPlantEnumDialogOuter">
            <div className="AddPlantEnumDialogInner">
              <TextField
                sx={primaryColorTextField}
                size="small"
                fullWidth
                label="Hardware"
                variant="outlined"
                value={hardwareActual}
                onChange={(e) => setHardwareActual(e.target.value)}
              />
              <IconButton
                sx={{ marginLeft: "10px" }}
                onClick={() => {
                  const newList = [...hardwareList];
                  newList.push(hardwareActual);
                  setHardwareList(newList);
                  setHardwareActual("");
                }}
              >
                <AddIcon sx={{ color: "#44a99a" }} />
              </IconButton>
            </div>
            <div className="AddPlantEnumDialogList">
              {hardwareList.map((item, index) => (
                <div key={index} className="AddPlantEnumDialogListItem">
                  <p className="AddPlantEnumDialogListItemText">{item}</p>
                  <IconButton
                    sx={{ marginLeft: "10px" }}
                    onClick={() => {
                      const newList = [...hardwareList];
                      newList.splice(index, 1);
                      setHardwareList(newList);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              ))}
            </div>
          </div>
          <div
            className="AddPlantEnumDialogOuter"
            style={{ marginTop: "20px" }}
          >
            <p className="AddPlantEnumDialogOuterTitle">Sensors</p>
            <div className="AddPlantEnumDialogInner">
              <TextField
                sx={primaryColorTextField}
                size="small"
                fullWidth
                label="Name"
                variant="outlined"
                value={sensorActualName}
                onChange={(e) => setSensorActualName(e.target.value)}
              />
              <IconButton
                sx={{ marginLeft: "10px" }}
                onClick={() => {
                  const newList = [...sensorList];
                  newList.push({
                    name: sensorActualName,
                    type: sensorActualType,
                    unit: sensorActualUnit,
                    hardware: sensorActualHardware,
                  });
                  setSensorList(newList);
                  setSensorActualHardware("");
                  setSensorActualName("");
                  setSensorActualType("");
                  setSensorActualUnit("");
                }}
              >
                <AddIcon sx={{ color: "#44a99a" }} />
              </IconButton>
            </div>
            <div className="AddPlantEnumDialogInner">
              <TextField
                sx={primaryColorTextField}
                size="small"
                fullWidth
                label="Hardware"
                variant="outlined"
                value={sensorActualHardware}
                onChange={(e) => setSensorActualHardware(e.target.value)}
              />
              <TextField
                sx={{ ...primaryColorTextField, marginLeft: "5px" }}
                size="small"
                fullWidth
                label="Category"
                variant="outlined"
                value={sensorActualType}
                select
              >
                {deviceSensorEnums.map((option) => (
                  <MenuItem
                    onClick={() => {
                      setSensorActualType(option.value);
                      setSensorActualUnit(option.unit);
                    }}
                    key={option.value}
                    value={option.value}
                  >
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </div>
            <div className="AddPlantEnumDialogList">
              {sensorList.map((item, index) => (
                <div key={index} className="AddPlantEnumDialogListItem">
                  <p className="AddPlantEnumDialogListItemText">
                    {item.name} - {item.type}
                  </p>
                  <IconButton
                    sx={{ marginLeft: "10px" }}
                    onClick={() => {
                      const newList = [...sensorList];
                      newList.splice(index, 1);
                      setSensorList(newList);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              ))}
            </div>
          </div>
          <div
            className="AddPlantEnumDialogOuter"
            style={{ marginTop: "20px" }}
          >
            <p className="AddPlantEnumDialogOuterTitle">IO</p>
            <div className="AddPlantEnumDialogInner">
              <TextField
                sx={primaryColorTextField}
                size="small"
                fullWidth
                label="Name"
                variant="outlined"
                value={ioActualName}
                onChange={(e) => setIoActualName(e.target.value)}
              />
              <IconButton
                sx={{ marginLeft: "10px" }}
                onClick={() => {
                  const newList = [...ioList];
                  newList.push({
                    name: ioActualName,
                    type: ioActualType,
                    hardware: ioActualHardware,
                  });
                  setIoList(newList);
                  setIoActualName("");
                  setIoActualType("");
                  setIoActualHardware("");
                }}
              >
                <AddIcon sx={{ color: "#44a99a" }} />
              </IconButton>
            </div>
            <div className="AddPlantEnumDialogInner">
              <TextField
                sx={primaryColorTextField}
                size="small"
                fullWidth
                label="Hardware"
                variant="outlined"
                value={ioActualHardware}
                onChange={(e) => setIoActualHardware(e.target.value)}
              />
              <TextField
                sx={{ ...primaryColorTextField, marginLeft: "5px" }}
                size="small"
                fullWidth
                label="Category"
                variant="outlined"
                value={ioActualType}
                select
                onChange={(e) => setIoActualType(e.target.value)}
              >
                {deviceIoEnums.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </div>
            <div className="AddPlantEnumDialogList">
              {ioList.map((item, index) => (
                <div key={index} className="AddPlantEnumDialogListItem">
                  <p className="AddPlantEnumDialogListItemText">
                    {item.name} - {item.type}
                  </p>
                  <IconButton
                    sx={{ marginLeft: "10px" }}
                    onClick={() => {
                      const newList = [...ioList];
                      newList.splice(index, 1);
                      setIoList(newList);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              ))}
            </div>
          </div>
          <div
            className="AddPlantEnumDialogOuter"
            style={{ marginTop: "40px" }}
          >
            <p className="AddPlantEnumDialogOuterTitle">Icon</p>
            <div {...getRootProps({ className: "Dropzone" })}>
              <input {...getInputProps()} />
              <p
                className="AddPlantEnumDialogOuterSub"
                style={{ marginBottom: "10px" }}
              >
                Drag 'n' drop a Icon-image here, or click to select
              </p>
            </div>
            {iconPath && (
              <div className="AddPlantEnumDialogList">
                <p className="AddPlantEnumDialogOuterTitle">
                  Chosen icon - Click to delete
                </p>
                <div
                  onClick={() => setIconPath(null)}
                  className="AddPlantEnumDialogListImage"
                >
                  <ImageLoader
                    imgUrl={URL.createObjectURL(iconPath)}
                    radiusStyle={"5px"}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="AddPlantEnumDialogOuter">
            <p className="AddPlantEnumDialogOuterTitle">Images</p>
            <div {...getRootPropsImageArray({ className: "Dropzone" })}>
              <input {...getInputPropsImageArray()} />
              <p className="AddPlantEnumDialogOuterSub">
                Drag 'n' drop up to 5 images here, or click to select
              </p>
            </div>
            {imagePathArray.length > 0 && (
              <p className="AddPlantEnumDialogOuterTitle">
                Chosen images - Click to delete
              </p>
            )}
            {imagePathArray.length > 0 && (
              <div className="AddPlantEnumDialogImageList">
                {imagePathArray.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => onDeleteFromImageArray(index)}
                    className="AddPlantEnumDialogListImage"
                  >
                    <ImageLoader
                      imgUrl={URL.createObjectURL(item)}
                      radiusStyle={"5px"}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          <div
            className="AddPlantEnumDialogInner"
            style={{ marginTop: "60px", justifyContent: "flex-end" }}
          >
            <Button
              disabled={dialogLoading}
              sx={{ marginLeft: "20px", marginRight: "20px" }}
              color="inherit"
              onClick={onReset}
            >
              RESET
            </Button>
            <Button
              disabled={dialogLoading}
              sx={{ marginLeft: "20px", marginRight: "20px" }}
              color="inherit"
              onClick={uploadDeviceEnum}
            >
              {dialogLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "ADD"
              )}
            </Button>
          </div>
        </div>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        key={"bottom" + "center"}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
      />
    </div>
  );
}

export default AddDeviceEnum;
